import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Token validation failed:', error);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const login = async (credentials) => {
    try {
      console.log('[AUTH] Login attempt:', credentials.email);
      const response = await authAPI.login(credentials);
      
      const { access_token, refresh_token } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      console.log('[AUTH] Tokens stored successfully');

      // Fetch user profile
      const userResponse = await authAPI.getMe();
      setUser(userResponse.data);
      setIsAuthenticated(true);
      
      toast.success('Login successful!');
      console.log('[AUTH] Login completed successfully');
      return { success: true, user: userResponse.data };
    } catch (error) {
      console.error('[AUTH] Login failed:', error);
      
      let errorMsg = 'Login failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
      return { success: false, error: errorMsg, needsType: error.response?.data?.detail === '2FA code required' ? '2FA' : null };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Best-effort — clear local state regardless
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    setIsAuthenticated, // For backward compatibility
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
